import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Mic } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useSession } from '../hooks/useSession';
import { useVoiceSession } from '../hooks/useVoiceSession';
import ChatInterface from '../components/session/ChatInterface';
import ExtractionPanel from '../components/session/ExtractionPanel';
import VoiceInterface from '../components/session/VoiceInterface';
import SessionTaskPanel from '../components/session/SessionTaskPanel';
import MoodEnergyCard from '../components/mood/MoodEnergyCard';
import type { ManualTask } from '../components/session/SessionTaskPanel';
import type { SessionType } from '../types';
import { SESSION_LABELS } from '../utils/sessionDisplay';
import { getPreviousDayMoodEnergyDefaults, saveCheckInSignals, toDateKey } from '../services/dailyLog';
import { updateSessionStateSnapshot } from '../services/sessions';

export default function SessionPage() {
  const { user } = useAuth();
  const { profile } = useProfile(user?.uid ?? null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionType = (searchParams.get('type') ?? 'morning_checkin') as SessionType;
  const mode = searchParams.get('mode') ?? 'voice';
  const continueSessionId = searchParams.get('continue');

  const textSession = useSession(user?.uid ?? null);

  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [endConfirmSource, setEndConfirmSource] = useState<'button' | 'voice' | 'close'>('button');
  const [autoEnding, setAutoEnding] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [initialMoodEnergy, setInitialMoodEnergy] = useState({ mood: 5, energy: 5 });
  const openEndConfirm = useCallback((source: 'button' | 'voice' | 'close') => {
    setEndConfirmSource(source);
    setShowEndConfirm(true);
  }, []);

  const voiceSession = useVoiceSession(user?.uid ?? null, {
    onEndIntent: () => openEndConfirm('voice'),
    onAutoEnd: () => setAutoEnding(true),
  });

  const isVoice = mode === 'voice';
  const supportsTaskPanel = (
    sessionType === 'task_planning'
    || sessionType === 'morning_checkin'
    || sessionType === 'evening_checkin'
    || sessionType === 'ad_hoc'
  );

  const realtimeExtractions = isVoice
    ? voiceSession.realtimeExtractions
    : textSession.realtimeExtractions;
  const actionItemExtractions = realtimeExtractions.filter((e) => e.type === 'action_item');

  const [manualTasks, setManualTasks] = useState<ManualTask[]>([]);

  const handleAddManualTask = useCallback((text: string) => {
    const id = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setManualTasks(prev => [...prev, { id, text, completed: false }]);

    if (isVoice && voiceSession.status === 'active') {
      voiceSession.sendText(
        `[The user just added a task to their list: "${text}". Acknowledge this briefly and continue the conversation.]`
      );
    }
  }, [isVoice, voiceSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleManualTask = useCallback((id: string) => {
    setManualTasks(prev =>
      prev.map(t => (t.id === id ? { ...t, completed: !t.completed } : t))
    );
  }, []);

  // Auto-start on mount
  useEffect(() => {
    if (!user) return;
    if (isVoice) {
      if (continueSessionId) {
        voiceSession.resume(sessionType, continueSessionId);
      } else {
        voiceSession.start(sessionType);
      }
    } else if (continueSessionId) {
      textSession.resume(continueSessionId);
    } else {
      textSession.start(sessionType);
    }
  }, [user, sessionType, isVoice, continueSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open side panel when extraction arrives.
  useEffect(() => {
    if (realtimeExtractions.length > 0) setPanelOpen(true);
  }, [realtimeExtractions.length]);

  useEffect(() => {
    if (!user || (sessionType !== 'morning_checkin' && sessionType !== 'evening_checkin')) return;
    let cancelled = false;
    void (async () => {
      try {
        const defaults = await getPreviousDayMoodEnergyDefaults(user.uid, new Date());
        if (!cancelled) setInitialMoodEnergy(defaults);
      } catch {
        if (!cancelled) setInitialMoodEnergy({ mood: 5, energy: 5 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, sessionType]);

  // For task-capable sessions, open the task panel when action items are captured.
  useEffect(() => {
    if (!supportsTaskPanel) return;
    if (actionItemExtractions.length > 0) setPanelOpen(true);
  }, [supportsTaskPanel, actionItemExtractions.length]);

  // Auto-end when both user and coach have said goodbye
  useEffect(() => {
    if (!autoEnding || !isVoice) return;
    if (voiceSession.isAssistantSpeaking) return;
    setShowEndConfirm(false);
    const timer = setTimeout(() => {
      voiceSession.end();
      setAutoEnding(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [autoEnding, isVoice, voiceSession.isAssistantSpeaking]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate away immediately when session completes (extraction runs in background)
  useEffect(() => {
    const completed = isVoice
      ? voiceSession.status === 'completed'
      : textSession.status === 'completed';

    if (completed) {
      navigate('/');
    }
  }, [isVoice, voiceSession.status, textSession.status, navigate]);

  function handleEndSession() {
    setShowEndConfirm(false);
    if (isVoice) {
      voiceSession.end();
    } else {
      textSession.end();
    }
  }

  async function handleDiscardSession() {
    setShowEndConfirm(false);
    setAutoEnding(false);
    voiceSession.resetEndDetection();
    try {
      if (isVoice) {
        await voiceSession.discard();
      } else {
        await textSession.discard();
      }
      navigate('/');
    } catch {
      // The hook already surfaces a user-facing error banner.
    }
  }

  const isActive = isVoice
    ? voiceSession.status === 'active'
    : textSession.status === 'active';

  const isBusy = isVoice
    ? voiceSession.status === 'connecting'
    : textSession.status === 'starting' || textSession.status === 'sending';

  const activeSessionId = isVoice ? voiceSession.sessionId : textSession.sessionId;
  const isCheckInType = sessionType === 'morning_checkin' || sessionType === 'evening_checkin';
  const moodEnergyEnabled = sessionType === 'morning_checkin'
    ? profile?.preferences?.showMoodEnergyMorning ?? true
    : sessionType === 'evening_checkin'
      ? profile?.preferences?.showMoodEnergyEvening ?? true
      : false;
  const showMoodEnergyCard = isCheckInType
    && moodEnergyEnabled
    && Boolean(activeSessionId);

  const sessionError = isVoice ? voiceSession.error : textSession.error;
  const showTaskPanel = supportsTaskPanel
    && (
      sessionType === 'task_planning'
      || ((actionItemExtractions.length > 0 || manualTasks.length > 0) && panelOpen)
    );

  async function handleAutoSaveMoodEnergy(payload: { mood?: number; energy?: number }) {
    if (!user || !activeSessionId || !isCheckInType) return;
    const hasSignal = typeof payload.mood === 'number' || typeof payload.energy === 'number';
    if (!hasSignal) return;
    try {
      await updateSessionStateSnapshot(user.uid, activeSessionId, payload);

      await saveCheckInSignals(
        user.uid,
        toDateKey(new Date()),
        sessionType === 'morning_checkin' ? 'morning_checkin' : 'evening_checkin',
        {
          sessionId: activeSessionId,
          completedAt: Timestamp.now(),
          mood: payload.mood,
          energy: payload.energy,
        }
      );
    } catch (err) {
      throw (err instanceof Error ? err : new Error('Failed to save mood and energy'));
    }
  }

  return (
    <div className="flex flex-col h-screen bg-[var(--color-background)]">
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] shrink-0"
        style={{ background: 'var(--color-background)' }}
      >
        <button
          onClick={() => {
            if (isActive || isBusy) {
              openEndConfirm('close');
              return;
            }
            navigate('/');
          }}
          className="p-2 -ml-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)] transition-colors"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{
              background: isActive ? 'var(--color-success)' : 'var(--color-text-muted)',
            }}
          />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {SESSION_LABELS[sessionType]}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {realtimeExtractions.length > 0 && (
            <button
              onClick={() => setPanelOpen(p => !p)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                background: panelOpen ? 'var(--color-brand-primary)' : 'var(--color-surface)',
                color: panelOpen ? 'white' : 'var(--color-text-secondary)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: panelOpen ? 'white' : 'var(--color-success)' }}
              />
              {realtimeExtractions.length}
            </button>
          )}

          {!isVoice && isActive && !continueSessionId && (
            <>
              <button
                onClick={() => navigate(`/session?type=${sessionType}&mode=voice`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-[var(--color-surface)] active:scale-95"
                style={{ color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
              >
                <Mic size={13} />
                Voice Mode
              </button>
              <button
                onClick={() => openEndConfirm('button')}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
              >
                End
              </button>
            </>
          )}
        </div>
      </header>

      {showMoodEnergyCard && (
        <MoodEnergyCard
          initialMood={initialMoodEnergy.mood}
          initialEnergy={initialMoodEnergy.energy}
          onAutoSave={handleAutoSaveMoodEnergy}
        />
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        <div className={`flex flex-col ${panelOpen || sessionType === 'task_planning' ? 'flex-1 md:w-[60%]' : 'flex-1'} overflow-hidden`}>
          {isVoice ? (
            <VoiceInterface
              status={voiceSession.status}
              transcript={voiceSession.transcript}
              isMuted={voiceSession.isMuted}
              isAssistantSpeaking={voiceSession.isAssistantSpeaking}
              onToggleMute={voiceSession.toggleMute}
              onEnd={() => openEndConfirm('button')}
              onSendText={voiceSession.sendText}
              onSwitchToText={() => {
                voiceSession.disconnect();
                navigate(`/session?type=${sessionType}&mode=text`);
              }}
            />
          ) : (
            <>
              {textSession.status === 'starting' ? (
                <div className="flex flex-1 items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full animate-pulse"
                      style={{ background: 'var(--color-surface)' }}
                    />
                    <p className="text-sm text-[var(--color-text-muted)]">Starting your session…</p>
                  </div>
                </div>
              ) : (
                <ChatInterface
                  messages={textSession.messages}
                  onSend={textSession.send}
                  disabled={isBusy}
                  placeholder={isBusy ? 'Flank is thinking…' : 'Reply to Flank…'}
                />
              )}
            </>
          )}
        </div>

        {showTaskPanel && (
          <div
            className="hidden md:flex w-72 border-l border-[var(--color-border)] overflow-hidden flex-col"
            style={{ background: 'var(--color-surface)' }}
          >
            <SessionTaskPanel
              extractions={realtimeExtractions}
              manualTasks={manualTasks}
              onAddTask={handleAddManualTask}
              onToggleTask={handleToggleManualTask}
            />
          </div>
        )}

        {panelOpen && !showTaskPanel && (
          <div
            className="w-full md:w-64 border-l border-[var(--color-border)] overflow-hidden flex flex-col"
            style={{ background: 'var(--color-surface)' }}
          >
            <ExtractionPanel
              extractions={realtimeExtractions}
              isEnding={isBusy && !isVoice}
            />
          </div>
        )}
      </div>

      {/* Error banner */}
      {sessionError && (
        <div
          className="px-4 py-3 text-sm text-[var(--color-error)] border-t border-[var(--color-border)]"
          style={{ background: 'var(--color-error-subtle)' }}
        >
          {sessionError}
        </div>
      )}

      {/* End confirmation modal */}
      {showEndConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-sm rounded-2xl p-6 flex flex-col gap-4"
            style={{ background: 'var(--color-background)' }}
          >
            <div>
              <h3 className="font-semibold text-[var(--color-text-primary)]">
                {endConfirmSource === 'close'
                  ? 'End this session?'
                  : endConfirmSource === 'voice'
                    ? 'Would you like to end the session?'
                    : 'End session?'}
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {endConfirmSource === 'close'
                  ? 'Choose whether to continue, discard this conversation, or finish and analyze it now.'
                  : endConfirmSource === 'voice'
                    ? 'You can keep talking if you like, or wrap up and get to it.'
                    : 'Your insights will be saved and your graph updated in the background.'}
              </p>
            </div>
            {endConfirmSource === 'close' ? (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setShowEndConfirm(false); setAutoEnding(false); voiceSession.resetEndDetection(); }}
                  className="flank-btn flank-btn-secondary"
                >
                  Continue session
                </button>
                <button onClick={handleEndSession} className="flank-btn flank-btn-primary">
                  Finish and analyze
                </button>
                <button
                  onClick={handleDiscardSession}
                  className="w-full py-2.5 rounded-xl text-sm font-medium transition-colors"
                  style={{
                    background: 'var(--color-error-subtle)',
                    color: 'var(--color-error)',
                    border: '1px solid var(--color-error)',
                  }}
                >
                  Discard session
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowEndConfirm(false); setAutoEnding(false); voiceSession.resetEndDetection(); }}
                  className="flank-btn flank-btn-secondary flex-1"
                >
                  {endConfirmSource === 'voice' ? 'Keep going' : 'Continue'}
                </button>
                <button onClick={handleEndSession} className="flank-btn flank-btn-primary flex-1">
                  {endConfirmSource === 'voice' ? 'Yes, end session' : 'End session'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
